import { TnbUser } from './tnb-user';
import { tap } from 'rxjs/operators';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { WestnetzSidenavService } from '../common/westnetz-sidenav/westnetz-sidenav.service';
import { UserRoles } from 'src/app/domain/model/roles.enum';
import { HttpClient } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {

    private httpClient = inject(HttpClient);
    private logoutUrl = environment.API_URL + '/auth/logout';

    private loggedInUser$ = new ReplaySubject<TnbUser>(1);
    private loggedInUser: TnbUser;

    private sideNavService = inject(WestnetzSidenavService);

    constructor() {
        this.loggedInUser$
            .pipe(
                tap((user) => {
                    if (user) {
                        if (!this.isTdlUser(user)) {
                            console.error('not a tdl user');
                            this.logout(); // Logout if not tdl user
                        }
                    }
                }),
            )
            .subscribe();
    }

    public get user$(): Observable<TnbUser> {
        return this.loggedInUser$;
    }

    public get user(): TnbUser {
        return this.loggedInUser;
    }

    public get loginUrl(): string {
        return environment.oauth.loginUrl;
    }

    public get isAuthenticated(): boolean {
        return (!!this.loggedInUser);
    }

    public setPrincipal(user: TnbUser) {
        this.loggedInUser = user;
        this.loggedInUser$.next(this.loggedInUser);
    }

    public clear() {
        this.loggedInUser = null;
        this.loggedInUser$.next(this.loggedInUser);
    }

    public containsValidRoleForThisApplication(): boolean {
        if (!this.isAuthenticated) {
            return true; // no session is invalid.
        }
        return this.isTdlUser(this.loggedInUser);
    }

    public isTdlUser(user: TnbUser): boolean {
        return this.isRoleInPermissions([
            UserRoles.TDL_ADMIN_USER,
            UserRoles.TDL_VNB_USER,
            UserRoles.TDL_WESTNETZ_USER,
        ],
        user?.roles);
    }

    public checkRole(allowedRoles: UserRoles | UserRoles[]): boolean {
        return this.isRoleInPermissions(Array.isArray(allowedRoles) ? allowedRoles : [
            allowedRoles,
        ], this.loggedInUser.roles);
    }

    private isRoleInPermissions(allowedRoles: UserRoles[], userRoles: any): boolean {
        if (Array.isArray(userRoles)) {
            return allowedRoles.some(role => userRoles.includes(role));
        }
        return false;
    }

    public login() {
        window.location.href = this.loginUrl;
    }

    public async logout() {
        this.loggedInUser = null;
        this.sideNavService.closeRightnav();

        this.clear();
        await firstValueFrom(this.httpClient.post(this.logoutUrl, ''));
        window.location.href = environment.oauth.logoutUrl;
    }

    public isWestnetzAdmin(user: TnbUser) {
        return this.isRoleInPermissions([
            UserRoles.TDL_ADMIN_USER,
        ], user?.roles);
    }

    public isWestnetzUser(user: TnbUser) {
        return this.isRoleInPermissions([
            UserRoles.TDL_WESTNETZ_USER,
        ], user?.roles);
    }

    public async isAuthenticatedTryRespawnSession(): Promise<boolean> {
        if (!this.isAuthenticated) {
            const user = await firstValueFrom(this.httpClient.get<TnbUser | any>(environment.API_URL + '/profile'));
            this.setPrincipal(user);
        }
        return this.isAuthenticated;
    }
}
